import {
  CardElement,
  Elements,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  createStyles,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Theme,
  Typography,
} from "@material-ui/core";
import { useDispatch, useSelector } from "react-redux";
import EnumUtils from "utils/EnumUtils";
import { PlanType } from "types/enum/PlanType";
import { makeStyles } from "@material-ui/core/styles";
import useModal from "hooks/useModal";
import Modal from "components/Modal";
import PaymentApi from "api/PaymentApi";
import RequestUtils from "utils/RequestUtils";
import { Alert, AlertTitle } from "@material-ui/lab";
import { updatePlan } from "redux/actions/account";
import StaffRole from "types/enum/StaffRole";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";

const Payment = () => {
  const { t } = useTranslation("payment");
  const [clientSecret, setClientSecret] = useState("");
  const history = useHistory();
  const staff = useSelector((state) => state.account?.staff);
  const stripePromise = loadStripe(
    process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || ""
  );
  const [cardPaymentModal, setCardPaymentModal] = useModal("cardPaymentModal");
  // ステータス再取得トリガ。cancel / uncancel / card登録 後に bump する。
  const [statusVersion, setStatusVersion] = useState(0);
  const bumpStatus = () => setStatusVersion((v) => v + 1);

  useEffect(() => {
    if (!clientSecret) return;
    setCardPaymentModal(true);
  }, [clientSecret]);

  if (
    ![StaffRole.notelMaster, StaffRole.clientMaster].includes(
      EnumUtils.mapToEnum(StaffRole, staff?.role) ||
        StaffRole.clientShopPartTime
    )
  ) {
    // ★止められて 連れて来られた時は 押し返さない★
    //   押し返すと TOP が 402 を 返し、また ここへ 来る ＝ 永久に 往復する。
    //   この方は お支払いの お手続きが できないので、その旨だけ お伝えする。
    if (RequestUtils.wasSentToPaymentBySuspension()) {
      return (
        <Box mx={5} mt={5}>
          <Alert severity="error">
            <AlertTitle>ご利用を一時停止しております</AlertTitle>
            お支払いが確認できないため、ご利用を一時停止しております。
            <br />
            お手続きは管理者の権限をお持ちのアカウントからお願いいたします。
          </Alert>
        </Box>
      );
    }
    history.push("/");
    return null;
  }
  return (
    <>
      <SuspendedBanner statusVersion={statusVersion} />
      <CancellationBanner
        statusVersion={statusVersion}
        onChange={bumpStatus}
      />
      <TrialCardRegistrationCta
        statusVersion={statusVersion}
        onChange={bumpStatus}
      />
      <PaymentForm
        setClientSecret={setClientSecret}
        statusVersion={statusVersion}
        onChange={bumpStatus}
      />
      {clientSecret && (
        <Modal
          show={cardPaymentModal.show}
          setShow={setCardPaymentModal}
          title={t("modal.cardRegistration")}
          disableEnforceFocus
        >
          <Typography style={{ margin: "20px 0" }}>
            {t("modal.dailyProration", {
              price: cardPaymentModal?.meta?.price,
            })}
          </Typography>
          <Elements stripe={stripePromise} options={{ clientSecret }}>
            <CheckoutForm clientSecret={clientSecret} />
          </Elements>
        </Modal>
      )}
    </>
  );
};

type PaymentFormProps = {
  setClientSecret: any;
  statusVersion: number;
  onChange: () => void;
};
const PaymentForm = ({
  setClientSecret,
  statusVersion,
  onChange,
}: PaymentFormProps) => {
  const { t } = useTranslation("payment");
  const currentPlan = useSelector(
    (state) => state.account.staff.company.companyGroup.plan
  );
  const priceStr = (amount: string) => t("values.price", { amount });
  const priceRangeStr = (amount: string) =>
    t("values.priceRange", { amount });
  const itemsStr = (count: string) => t("values.items", { count });
  const peopleStr = (count: string) => t("values.people", { count });
  const companiesStr = (count: string) => t("values.companies", { count });
  const yearsStr = (count: string) => t("values.years", { count });
  const perItemYenStr = (amount: string) =>
    t("values.perItemYen", { amount });
  const included = t("values.included");
  const excluded = t("values.excluded");
  const unlimited = t("values.unlimited");
  const free = t("values.free");
  const dash = t("values.dash");
  const bodyList = [
    {
      title: t("rows.monthlyFee"),
      light: priceStr("5,000"),
      standard: priceStr("15,000"),
      premium: priceStr("30,000"),
      enterprise: priceRangeStr("50,000"),
    },
    {
      title: t("rows.yearlyFee"),
      light: dash,
      standard: priceStr("150,000"),
      premium: priceStr("300,000"),
      enterprise: priceRangeStr("600,000"),
    },
    {
      title: t("rows.groupFeature"),
      light: excluded,
      standard: included,
      premium: included,
      enterprise: included,
    },
    {
      title: t("rows.orderHistoryRetention"),
      light: yearsStr("1"),
      standard: yearsStr("3"),
      premium: yearsStr("5"),
      enterprise: unlimited,
    },
    {
      title: t("rows.shopManagement"),
      light: itemsStr("2"),
      standard: itemsStr("10"),
      premium: itemsStr("25"),
      enterprise: unlimited,
    },
    {
      title: t("rows.cti"),
      light: included,
      standard: included,
      premium: included,
      enterprise: included,
    },
    {
      title: t("rows.companyManagement"),
      light: companiesStr("1"),
      standard: companiesStr("3"),
      premium: companiesStr("5"),
      enterprise: unlimited,
    },
    {
      title: t("rows.staffManagement"),
      light: peopleStr("2"),
      standard: peopleStr("3"),
      premium: peopleStr("10"),
      enterprise: unlimited,
    },
    {
      title: t("rows.board"),
      light: included,
      standard: included,
      premium: included,
      enterprise: included,
    },
    {
      title: t("rows.aiSupport"),
      light: included,
      standard: included,
      premium: included,
      enterprise: included,
    },
    {
      title: t("rows.noticeManagement"),
      light: itemsStr("5"),
      standard: itemsStr("10"),
      premium: itemsStr("25"),
      enterprise: unlimited,
    },
    {
      title: t("rows.guestManagement"),
      light: itemsStr("5000"),
      standard: itemsStr("15000"),
      premium: unlimited,
      enterprise: unlimited,
    },
    {
      title: t("rows.pointManagement"),
      light: itemsStr("5000"),
      standard: itemsStr("15000"),
      premium: unlimited,
      enterprise: unlimited,
    },
    {
      title: t("rows.castManagement"),
      light: peopleStr("20"),
      standard: peopleStr("40"),
      premium: unlimited,
      enterprise: unlimited,
    },
    {
      title: t("rows.castNameManagement"),
      light: itemsStr("30"),
      standard: itemsStr("80"),
      premium: unlimited,
      enterprise: unlimited,
    },
    {
      title: t("rows.castCategoryManagement"),
      light: itemsStr("8"),
      standard: unlimited,
      premium: unlimited,
      enterprise: unlimited,
    },
    {
      title: t("rows.nominationTypeManagement"),
      light: itemsStr("6"),
      standard: unlimited,
      premium: unlimited,
      enterprise: unlimited,
    },
    {
      title: t("rows.classManagement"),
      light: itemsStr("6"),
      standard: itemsStr("15"),
      premium: unlimited,
      enterprise: unlimited,
    },
    {
      title: t("rows.driverManagement"),
      light: peopleStr("5"),
      standard: peopleStr("15"),
      premium: unlimited,
      enterprise: unlimited,
    },
    {
      title: t("rows.optionManagement"),
      light: itemsStr("25"),
      standard: unlimited,
      premium: unlimited,
      enterprise: unlimited,
    },
    {
      title: t("rows.discountManagement"),
      light: itemsStr("5"),
      standard: itemsStr("10"),
      premium: unlimited,
      enterprise: unlimited,
    },
    {
      title: t("rows.mediumManagement"),
      light: itemsStr("10"),
      standard: itemsStr("20"),
      premium: unlimited,
      enterprise: unlimited,
    },
    {
      title: t("rows.areaHotelManagement"),
      light: unlimited,
      standard: unlimited,
      premium: unlimited,
      enterprise: unlimited,
    },
    {
      title: t("rows.cosplayManagement"),
      light: itemsStr("10"),
      standard: itemsStr("20"),
      premium: unlimited,
      enterprise: unlimited,
    },
    {
      title: t("rows.courseManagement"),
      light: unlimited,
      standard: unlimited,
      premium: unlimited,
      enterprise: unlimited,
    },
    {
      title: t("rows.additionalTimeManagement"),
      light: unlimited,
      standard: unlimited,
      premium: unlimited,
      enterprise: unlimited,
    },
    {
      title: t("rows.guestCategoryManagement"),
      light: itemsStr("3"),
      standard: itemsStr("5"),
      premium: unlimited,
      enterprise: unlimited,
    },
    {
      title: t("rows.bookEmailManagement"),
      light: included,
      standard: included,
      premium: included,
      enterprise: included,
    },
    {
      title: t("rows.chatSupport"),
      light: free,
      standard: free,
      premium: free,
      enterprise: free,
    },
    {
      title: t("rows.onlineSupport"),
      light: free,
      standard: free,
      premium: free,
      enterprise: free,
    },
    {
      title: t("rows.phoneSupport"),
      light: free,
      standard: free,
      premium: free,
      enterprise: free,
    },
    {
      title: t("rows.visitSupport"),
      light: excluded,
      standard: included,
      premium: included,
      enterprise: included,
    },
    {
      title: t("rows.dataImportProxy"),
      light: perItemYenStr("5"),
      standard: perItemYenStr("3"),
      premium: unlimited,
      enterprise: unlimited,
    },
  ];
  const useStyles = makeStyles((theme: Theme) =>
    createStyles({
      button: {
        margin: theme.spacing(1),
      },
      cell: {
        position: "sticky",
      },
    })
  );
  const classes = useStyles();
  const [, setPlanSelectModal] = useModal("planSelectModal");

  return (
    <Paper style={{ margin: "40px" }}>
      {EnumUtils.mapToEnum(PlanType, currentPlan) === PlanType.enterprise && (
        <Alert>
          <AlertTitle>{t("alerts.enterpriseContact")}</AlertTitle>
        </Alert>
      )}
      <Alert color={"info"}>
        <AlertTitle>{t("alerts.domainWhitelistTitle")}</AlertTitle>
        <Box display={"flex"} flexDirection={"column"}>
          <Typography>{t("alerts.domainWhitelistBody")}</Typography>
          <Typography>notel.com</Typography>
          <Typography>stripe.com</Typography>
        </Box>
      </Alert>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell />
            <TableCell>{t("plans.light")}</TableCell>
            <TableCell>{t("plans.standard")}</TableCell>
            <TableCell>{t("plans.premium")}</TableCell>
            <TableCell>{t("plans.enterprise")}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          <TableRow>
            <TableCell />
            <TableCell>
              <Button
                variant="contained"
                className={classes.button}
                color="primary"
                onClick={() => setPlanSelectModal(true, { plan: "light" })}
                disabled={[PlanType.light, PlanType.enterprise].includes(
                  EnumUtils.mapToEnum(PlanType, currentPlan) || PlanType.free
                )}
              >
                {EnumUtils.mapToEnum(PlanType, currentPlan) === PlanType.light
                  ? t("actions.currentPlan")
                  : t("actions.selectPlan")}
              </Button>
            </TableCell>
            <TableCell>
              <Button
                variant="contained"
                className={classes.button}
                color="primary"
                onClick={() => setPlanSelectModal(true, { plan: "starter" })}
                disabled={[PlanType.starter, PlanType.enterprise].includes(
                  EnumUtils.mapToEnum(PlanType, currentPlan) || PlanType.free
                )}
              >
                {EnumUtils.mapToEnum(PlanType, currentPlan) === PlanType.starter
                  ? t("actions.currentPlan")
                  : t("actions.selectPlan")}
              </Button>
            </TableCell>
            <TableCell>
              <Button
                variant="contained"
                className={classes.button}
                color="primary"
                onClick={() => setPlanSelectModal(true, { plan: "premium" })}
                disabled={[PlanType.premium, PlanType.enterprise].includes(
                  EnumUtils.mapToEnum(PlanType, currentPlan) || PlanType.free
                )}
              >
                {EnumUtils.mapToEnum(PlanType, currentPlan) === PlanType.premium
                  ? t("actions.currentPlan")
                  : t("actions.selectPlan")}
              </Button>
            </TableCell>
            <TableCell>
              <Button
                variant="contained"
                className={classes.button}
                color="primary"
                onClick={() => setPlanSelectModal(true, { plan: "enterprise" })}
                disabled={
                  EnumUtils.mapToEnum(PlanType, currentPlan) ===
                  PlanType.enterprise
                }
              >
                {EnumUtils.mapToEnum(PlanType, currentPlan) ===
                PlanType.enterprise
                  ? t("actions.currentPlan")
                  : t("actions.inquire")}
              </Button>
            </TableCell>
          </TableRow>
          {bodyList.map((body, index) => (
            <TableRow key={index}>
              <TableCell>{body.title}</TableCell>
              <TableCell>{body.light}</TableCell>
              <TableCell>{body.standard}</TableCell>
              <TableCell>{body.premium}</TableCell>
              <TableCell>{body.enterprise}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <CancelPlanSection
        statusVersion={statusVersion}
        onChange={onChange}
      />
      <PlanSelectModal setClientSecret={setClientSecret} />
      <PaymentSendMailModal />
    </Paper>
  );
};

type CancelSectionProps = { statusVersion: number; onChange: () => void };
const CancelPlanSection = ({
  statusVersion,
  onChange,
}: CancelSectionProps) => {
  const { t } = useTranslation("payment");
  const companyId = useSelector((state) => state.account.staff.companyId);
  const currentPlan = useSelector(
    (state) => state.account.staff.company.companyGroup.plan
  );
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [ack, setAck] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [willEnd, setWillEnd] = useState<boolean | null>(null);

  useEffect(() => {
    if (
      EnumUtils.mapToEnum(PlanType, currentPlan) === PlanType.free ||
      EnumUtils.mapToEnum(PlanType, currentPlan) === PlanType.enterprise
    ) {
      return;
    }
    (async () => {
      try {
        const res = await PaymentApi.getStatus(companyId);
        setWillEnd(res.willEnd);
      } catch (_e) {
        /* noop */
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, currentPlan, statusVersion]);

  // 停止状態 (free) or enterprise は解約ボタン出さない
  if (
    EnumUtils.mapToEnum(PlanType, currentPlan) === PlanType.free ||
    EnumUtils.mapToEnum(PlanType, currentPlan) === PlanType.enterprise
  ) {
    return null;
  }
  // 解約予定中は上部のバナーで状態表示してるので、ここの解約ボタンは隠す
  if (willEnd) return null;

  const reset = () => {
    setOpen(false);
    setStep(1);
    setAck(false);
    setError("");
  };

  const submit = async () => {
    setLoading(true);
    setError("");
    try {
      await PaymentApi.cancelSubscription(companyId);
      setOpen(false);
      setWillEnd(true);
      onChange();
    } catch (e: any) {
      setError(e?.message || t("cancel.errorGeneric"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box mx={5} my={4}>
      <Box display="flex" justifyContent="flex-end">
        <Button
          variant="outlined"
          color="default"
          onClick={() => setOpen(true)}
          style={{ color: "#b00020", borderColor: "#b00020" }}
        >
          {t("actions.cancelPlan")}
        </Button>
      </Box>
      <Modal show={open} setShow={(v: boolean) => (v ? setOpen(true) : reset())}>
        <Box display="flex" flexDirection="column" style={{ gap: 16, padding: 4 }}>
          {step === 1 && (
            <>
              <Typography variant="h6" style={{ color: "#b00020" }}>
                {t("cancel.step1Title")}
              </Typography>
              <Alert severity="warning">
                <AlertTitle>{t("cancel.step1AlertTitle")}</AlertTitle>
                {t("cancel.step1AlertBody")}
                <ul style={{ margin: "8px 0 0 0", paddingLeft: 20 }}>
                  <li>{t("cancel.step1ItemStaff")}</li>
                  <li>{t("cancel.step1ItemGuest")}</li>
                  <li>{t("cancel.step1ItemMobile")}</li>
                </ul>
              </Alert>
              <Box display="flex" justifyContent="flex-end" style={{ gap: 8 }}>
                <Button variant="outlined" onClick={reset}>
                  {t("actions.cancel")}
                </Button>
                <Button
                  variant="contained"
                  style={{ backgroundColor: "#b00020", color: "#fff" }}
                  onClick={() => setStep(2)}
                >
                  {t("actions.next")}
                </Button>
              </Box>
            </>
          )}
          {step === 2 && (
            <>
              <Typography variant="h6" style={{ color: "#b00020" }}>
                {t("cancel.step2Title")}
              </Typography>
              <Alert severity="warning">
                <AlertTitle>{t("cancel.step2AlertTitle")}</AlertTitle>
                {t("cancel.step2AlertBody")}
              </Alert>
              <Box display="flex" justifyContent="flex-end" style={{ gap: 8 }}>
                <Button variant="outlined" onClick={() => setStep(1)}>
                  {t("actions.back")}
                </Button>
                <Button
                  variant="contained"
                  style={{ backgroundColor: "#b00020", color: "#fff" }}
                  onClick={() => setStep(3)}
                >
                  {t("actions.next")}
                </Button>
              </Box>
            </>
          )}
          {step === 3 && (
            <>
              <Typography variant="h6" style={{ color: "#b00020" }}>
                {t("cancel.step3Title")}
              </Typography>
              <Alert severity="error">
                <AlertTitle>{t("cancel.step3AlertTitle")}</AlertTitle>
                {t("cancel.step3AlertBody")}
              </Alert>
              <Box display="flex" alignItems="center">
                <Checkbox
                  checked={ack}
                  onChange={(e) => setAck(e.target.checked)}
                />
                <Typography variant="body2">
                  {t("cancel.step3Ack")}
                </Typography>
              </Box>
              {error && <Alert severity="error">{error}</Alert>}
              <Box display="flex" justifyContent="flex-end" style={{ gap: 8 }}>
                <Button
                  variant="outlined"
                  disabled={loading}
                  onClick={() => setStep(2)}
                >
                  {t("actions.back")}
                </Button>
                <Button
                  variant="contained"
                  style={{
                    backgroundColor: ack && !loading ? "#b00020" : "#ccc",
                    color: "#fff",
                  }}
                  disabled={!ack || loading}
                  onClick={submit}
                  startIcon={
                    loading ? (
                      <CircularProgress size={16} color="inherit" />
                    ) : null
                  }
                >
                  {loading
                    ? t("actions.processing")
                    : t("actions.confirmCancel")}
                </Button>
              </Box>
            </>
          )}
        </Box>
      </Modal>
    </Box>
  );
};

type CheckoutFormProps = {
  clientSecret: string;
};
const CheckoutForm = ({ clientSecret }: CheckoutFormProps) => {
  const { t } = useTranslation("payment");
  const [cardPaymentModal, setCardPaymentModal] = useModal("cardPaymentModal");
  const companyGroup = useSelector(
    (state) => state.account.staff.company.companyGroup
  );
  const companyId = useSelector((state) => state.account.staff.companyId);
  const stripe = useStripe();
  const elements = useElements();
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>("");
  const useStyles = makeStyles((theme: Theme) =>
    createStyles({
      button: {
        margin: theme.spacing(1),
      },
      cell: {
        position: "sticky",
      },
    })
  );
  const classes = useStyles();
  const handleSubmit = async (event: any) => {
    event.preventDefault();
    if (loading) return; // 多重送信防止
    setError("");

    if (!stripe || !elements) {
      setError(t("checkout.stripeLoading"));
      return;
    }

    setLoading(true);
    try {
      const result = await stripe.confirmCardPayment(clientSecret, {
        payment_method: {
          card: elements.getElement(CardElement) || { token: "" },
        },
      });
      if (result.error) {
        setError(result.error.message || t("checkout.cardError"));
        return;
      }
      if ((result as any).paymentIntent?.status === "succeeded") {
        await PaymentApi.updatePaymentMethod(
          companyId,
          (result as any).paymentIntent.payment_method || ""
        );
        dispatch(
          updatePlan(
            {
              ...companyGroup,
              plan: cardPaymentModal.meta?.plan,
            },
            companyId
          )
        );
        setCardPaymentModal(false, {});
      } else {
        setError(
          t("checkout.unexpectedStatus", {
            status: (result as any).paymentIntent?.status,
          })
        );
      }
    } catch (e) {
      setError((e as Error)?.message || t("checkout.networkError"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <CardElement options={{ hidePostalCode: true }} />
      {error && (
        <Alert severity="error" style={{ marginTop: 12 }}>
          {error}
        </Alert>
      )}
      {loading && (
        <Box
          display="flex"
          alignItems="center"
          justifyContent="center"
          marginTop={2}
          style={{ gap: 8 }}
        >
          <CircularProgress size={20} />
          <Typography variant="body2" color="textSecondary">
            {t("checkout.processingBody")}
          </Typography>
        </Box>
      )}
      <Box display="flex" justifyContent="center" margin={2}>
        <Button
          disabled={!stripe || loading}
          onClick={handleSubmit}
          variant="contained"
          className={classes.button}
          color="primary"
          startIcon={loading ? <CircularProgress size={16} /> : undefined}
        >
          {loading ? t("actions.processing") : t("actions.submit")}
        </Button>
      </Box>
    </>
  );
};

const PlanSelectModal = ({ setClientSecret }: { setClientSecret: any }) => {
  const { t } = useTranslation("payment");
  const [planSelectModal, setPlanSelectModal] = useModal("planSelectModal");
  const companyId = useSelector((state) => state.account.staff.companyId);
  const [, setCardPaymentModal] = useModal("cardPaymentModal");
  const [, setPaymentSendMailModal] = useModal("paymentSendMailModal");
  const companyGroup = useSelector(
    (state) => state.account.staff.company.companyGroup
  );
  const dispatch = useDispatch();
  const useStyles = makeStyles((theme: Theme) =>
    createStyles({
      button: {
        margin: theme.spacing(1),
      },
      cell: {
        position: "sticky",
      },
    })
  );
  const classes = useStyles();
  const priceStr = (amount: string) => t("values.price", { amount });
  const planPrice: any = {
    light: {
      month: {
        value: priceStr("5,000"),
        id: process.env.REACT_APP_LIGHT_MONTH_PRICE_ID,
      },
      // ライトプランは現状月払いのみ (年払い未定)
    },
    starter: {
      month: {
        value: priceStr("15,000"),
        id: process.env.REACT_APP_STANDARD_MONTH_PRICE_ID,
      },
      year: {
        value: priceStr("150,000"),
        id: process.env.REACT_APP_STANDARD_YEAR_PRICE_ID,
      },
    },
    premium: {
      month: {
        value: priceStr("30,000"),
        id: process.env.REACT_APP_PREMIUM_MONTH_PRICE_ID,
      },
      year: {
        value: priceStr("300,000"),
        id: process.env.REACT_APP_PREMIUM_YEAR_PRICE_ID,
      },
    },
  };

  const planLabel = (planKey: string) => {
    const mapped = EnumUtils.mapToEnum(PlanType, planKey || "");
    if (mapped === PlanType.light) return t("plans.light");
    if (mapped === PlanType.starter) return t("plans.standard");
    if (mapped === PlanType.premium) return t("plans.premium");
    if (mapped === PlanType.enterprise) return t("plans.enterprise");
    return String(mapped || planKey || "");
  };

  const clickAtCardPayment = async (planId: string, isAnnual: boolean) => {
    const customer = await PaymentApi.findOrCreateSubscription(
      companyId,
      planId,
      isAnnual
    );
    setClientSecret(customer.clientSecret);
    if (!customer.clientSecret) {
      dispatch(
        updatePlan(
          {
            ...companyGroup,
            plan: planSelectModal.meta?.plan,
          },
          companyId
        )
      );
    }
    setPlanSelectModal(false, {});
    setCardPaymentModal(true, {
      price: customer.price,
      plan: planSelectModal.meta?.plan,
    });
  };

  const clickAtPayment = async (
    planId: string,
    isAnnual: boolean,
    planType: PlanType
  ) => {
    await PaymentApi.findOrCreateSubscriptionOnMail(
      companyId,
      planId,
      isAnnual,
      planType
    );
    setPlanSelectModal(false, {});
    setPaymentSendMailModal(true);
  };

  return (
    <Modal
      show={planSelectModal.show}
      setShow={setPlanSelectModal}
      title={t("modal.planSwitch", {
        plan: planLabel(planSelectModal.meta?.plan || ""),
      })}
    >
      {EnumUtils.mapToEnum(PlanType, planSelectModal.meta?.plan || "") ===
      PlanType.enterprise ? (
        <Box margin={1}>
          <Typography>{t("alerts.enterpriseInquiry")}</Typography>
        </Box>
      ) : (
        <Box display="flex" flexDirection="column">
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography>
              {t("planPricing.monthly", {
                price:
                  planPrice[planSelectModal.meta?.plan || ""]?.month.value,
              })}
            </Typography>
            <Box display={"flex"}>
              <Button
                variant="contained"
                className={classes.button}
                color="primary"
                onClick={() =>
                  clickAtCardPayment(
                    String(
                      planPrice[planSelectModal.meta?.plan || ""]?.month.id
                    ),
                    false
                  )
                }
              >
                {t("actions.cardPayment")}
              </Button>
              <Button
                variant="contained"
                className={classes.button}
                color="primary"
                onClick={() =>
                  clickAtPayment(
                    String(
                      planPrice[planSelectModal.meta?.plan || ""]?.month.id
                    ),
                    false,
                    planSelectModal.meta?.plan
                  )
                }
              >
                {t("actions.konbiniPayment")}
              </Button>
            </Box>
          </Box>
          {planPrice[planSelectModal.meta?.plan || ""]?.year && (
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
            >
              <Typography>
                {t("planPricing.yearly", {
                  price:
                    planPrice[planSelectModal.meta?.plan || ""]?.year.value,
                })}
              </Typography>
              <Box display={"flex"}>
                <Button
                  variant="contained"
                  className={classes.button}
                  color="primary"
                  onClick={() =>
                    clickAtCardPayment(
                      String(
                        planPrice[planSelectModal.meta?.plan || ""]?.year.id
                      ),
                      true
                    )
                  }
                >
                  {t("actions.cardPayment")}
                </Button>
                <Button
                  variant="contained"
                  className={classes.button}
                  color="primary"
                  onClick={() =>
                    clickAtPayment(
                      String(
                        planPrice[planSelectModal.meta?.plan || ""]?.year.id
                      ),
                      true,
                      planSelectModal.meta?.plan
                    )
                  }
                >
                  {t("actions.konbiniPayment")}
                </Button>
              </Box>
            </Box>
          )}
        </Box>
      )}
    </Modal>
  );
};

const PaymentSendMailModal = () => {
  const { t } = useTranslation("payment");
  const [paymentSendMailModal, setPaymentSendMailModal] = useModal(
    "paymentSendMailModal"
  );
  const email = useSelector((state) => state.account.staff.company.email);
  return (
    <Modal show={paymentSendMailModal.show} setShow={setPaymentSendMailModal}>
      <Box>
        <Typography>{t("alerts.sendMail", { email })}</Typography>
      </Box>
    </Modal>
  );
};

/**
 * トライアル中のライトプラン会社向けカード登録 CTA。
 * Payment 画面トップに常設し、Stripe SetupIntent でカード保存 → updatePaymentMethod でデフォルト化。
 */
type CtaProps = { statusVersion: number; onChange: () => void };

/**
 * ★止められている時の 札 と 払う先★（2026-08-13・代表 / 経営で 決まった 案三）
 *
 * 止められた お客様は どの画面を 開いても ここへ 来る。ゆえに ここが
 * 「なぜ止まったか」と「何処で払うか」を 伝える 唯一の 場所になる。
 *
 * ★請求書リンクを 先に 出す★
 *   カードを 登録し直すより 手数が 少ない。金額と 期限も その頁に 出る。
 *   （代表が 同日 Stripe で「顧客が支払いを確定できるリンク」を オンにされた）
 * ★無い事も ある★
 *   初回の 請求が 一度も 払われず 期限切れになると、払える 請求書が 残らない。
 *   その時は 下の プラン選択から 入り直していただく。
 */
const SuspendedBanner = ({ statusVersion }: { statusVersion: number }) => {
  const companyId = useSelector((state) => state.account.staff.companyId);
  const [suspended, setSuspended] = useState(false);
  const [hostedInvoiceUrl, setHostedInvoiceUrl] = useState<string | null>(null);
  const [amountDueJpy, setAmountDueJpy] = useState<number | null>(null);

  useEffect(() => {
    // ★プランで 絞らない★ …… 止まるのは プランに 依らない。
    (async () => {
      try {
        const res = await PaymentApi.getStatus(companyId);
        setSuspended(!!res.suspended);
        setHostedInvoiceUrl(res.hostedInvoiceUrl ?? null);
        setAmountDueJpy(res.amountDueJpy ?? null);
      } catch (_e) {
        /* 出せないだけ。下の プラン表は 出る */
      }
    })();
  }, [companyId, statusVersion]);

  if (!suspended) return null;

  return (
    <Box mx={5} mt={5} mb={2}>
      <Alert
        severity="error"
        action={
          hostedInvoiceUrl ? (
            <Button
              color="primary"
              variant="contained"
              href={hostedInvoiceUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              お支払いへ
            </Button>
          ) : null
        }
      >
        <AlertTitle>ご利用を一時停止しております</AlertTitle>
        お支払いが確認できないため、ご利用を一時停止しております。
        {amountDueJpy !== null && (
          <>
            <br />
            お支払い予定額 {amountDueJpy.toLocaleString()} 円
          </>
        )}
        <br />
        {hostedInvoiceUrl
          ? "「お支払いへ」から お手続きいただくと、確認でき次第 自動で 再開いたします。"
          : "お支払いいただける請求書が残っていないため、下記からプランをお選び直しください。"}
      </Alert>
    </Box>
  );
};

/**
 * 解約予定の赤バナー。willEnd=true のとき上部に表示、解約取消ボタン付き。
 */
const CancellationBanner = ({ statusVersion, onChange }: CtaProps) => {
  const { t } = useTranslation("payment");
  const companyId = useSelector((state) => state.account.staff.companyId);
  const currentPlan = useSelector(
    (state) => state.account.staff.company.companyGroup.plan
  );
  const [willEnd, setWillEnd] = useState<boolean | null>(null);
  const [cancelAt, setCancelAt] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const refresh = async () => {
    try {
      const res = await PaymentApi.getStatus(companyId);
      setWillEnd(res.willEnd);
      setCancelAt(res.cancelAt);
    } catch (_e) {
      /* noop */
    }
  };

  useEffect(() => {
    if (
      EnumUtils.mapToEnum(PlanType, currentPlan) === PlanType.free ||
      EnumUtils.mapToEnum(PlanType, currentPlan) === PlanType.enterprise
    )
      return;
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, currentPlan, statusVersion]);

  if (!willEnd) return null;

  const onUncancel = async () => {
    setLoading(true);
    setError("");
    try {
      await PaymentApi.uncancelSubscription(companyId);
      setWillEnd(false);
      onChange();
    } catch (e: any) {
      setError(e?.message || t("cancel.errorUncancel"));
    } finally {
      setLoading(false);
    }
  };

  const fmt = (iso: string | null) => {
    if (!iso) return "";
    const d = new Date(iso);
    return `${d.getFullYear()}/${d.getMonth() + 1}/${d.getDate()}`;
  };

  return (
    <Box mx={5} mt={5} mb={2}>
      <Alert
        severity="error"
        action={
          <Button
            color="primary"
            variant="contained"
            disabled={loading}
            onClick={onUncancel}
            startIcon={
              loading ? <CircularProgress size={16} color="inherit" /> : null
            }
          >
            {loading ? t("actions.processing") : t("actions.uncancel")}
          </Button>
        }
      >
        <AlertTitle>
          {t("banner.willEndTitle", {
            date: fmt(cancelAt) || t("banner.periodEnd"),
          })}
        </AlertTitle>
        {t("banner.willEndBody")}
      </Alert>
      {error && (
        <Alert severity="error" style={{ marginTop: 8 }}>
          {error}
        </Alert>
      )}
    </Box>
  );
};

const TrialCardRegistrationCta = ({ statusVersion, onChange }: CtaProps) => {
  const { t } = useTranslation("payment");
  const stripePromise = loadStripe(
    process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || ""
  );
  const companyId = useSelector((state) => state.account.staff.companyId);
  const currentPlan = useSelector(
    (state) => state.account.staff.company.companyGroup.plan
  );
  const [modal, setModal] = useModal("cardRegistrationModal");
  const [clientSecret, setClientSecret] = useState("");
  const [opening, setOpening] = useState(false);
  const [error, setError] = useState("");
  const [hasPaymentMethod, setHasPaymentMethod] = useState<boolean | null>(
    null
  );
  const [justRegistered, setJustRegistered] = useState(false);

  const refresh = async () => {
    try {
      const res = await PaymentApi.getStatus(companyId);
      setHasPaymentMethod(res.hasPaymentMethod);
    } catch (_e) {
      // 失敗時は null のまま
    }
  };

  useEffect(() => {
    if (EnumUtils.mapToEnum(PlanType, currentPlan) === PlanType.light) {
      refresh();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPlan, companyId, statusVersion]);

  if (EnumUtils.mapToEnum(PlanType, currentPlan) !== PlanType.light) {
    return null;
  }
  // ステータス取得前は表示保留 (一瞬の誤表示回避)
  if (hasPaymentMethod === null) return null;

  const open = async () => {
    setOpening(true);
    setError("");
    try {
      const res = await PaymentApi.createSetupIntent(companyId);
      setClientSecret(res.clientSecret);
      setModal(true);
    } catch (e: any) {
      setError(e?.message || t("banner.cardOpenError"));
    } finally {
      setOpening(false);
    }
  };

  const onRegistered = () => {
    setModal(false);
    setClientSecret("");
    setHasPaymentMethod(true);
    setJustRegistered(true);
    refresh();
    onChange();
  };

  return (
    <Box mx={5} mt={5} mb={2}>
      {hasPaymentMethod ? (
        <Alert
          severity="success"
          action={
            <Button
              color="primary"
              variant="outlined"
              disabled={opening}
              onClick={open}
              startIcon={
                opening ? <CircularProgress size={16} color="inherit" /> : null
              }
            >
              {t("actions.changeCard")}
            </Button>
          }
        >
          <AlertTitle>{t("banner.cardRegisteredTitle")}</AlertTitle>
          {justRegistered
            ? t("banner.cardRegisteredJustBody")
            : t("banner.cardRegisteredBody")}
        </Alert>
      ) : (
        <Alert
          severity="info"
          action={
            <Button
              color="primary"
              variant="contained"
              disabled={opening}
              onClick={open}
              startIcon={
                opening ? <CircularProgress size={16} color="inherit" /> : null
              }
            >
              {t("actions.registerCard")}
            </Button>
          }
        >
          <AlertTitle>{t("banner.trialTitle")}</AlertTitle>
          {t("banner.trialBody")}
        </Alert>
      )}
      {error && (
        <Alert severity="error" style={{ marginTop: 8 }}>
          {error}
        </Alert>
      )}
      {clientSecret && (
        <Modal
          show={modal.show}
          setShow={setModal}
          title={
            hasPaymentMethod
              ? t("modal.cardInfoUpdate")
              : t("modal.cardInfoRegister")
          }
        >
          <Elements stripe={stripePromise} options={{ clientSecret }}>
            <SetupIntentForm
              clientSecret={clientSecret}
              onClose={() => {
                setModal(false);
                setClientSecret("");
              }}
              onSuccess={onRegistered}
            />
          </Elements>
        </Modal>
      )}
    </Box>
  );
};

type SetupIntentFormProps = {
  clientSecret: string;
  onClose: () => void;
  onSuccess?: () => void;
};
const SetupIntentForm = ({
  clientSecret,
  onClose,
  onSuccess,
}: SetupIntentFormProps) => {
  const { t } = useTranslation("payment");
  const stripe = useStripe();
  const elements = useElements();
  const companyId = useSelector((state) => state.account.staff.companyId);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async () => {
    if (!stripe || !elements) return;
    setLoading(true);
    setError("");
    try {
      const card = elements.getElement(CardElement);
      if (!card) throw new Error(t("setup.cardNotFound"));
      const result = await stripe.confirmCardSetup(clientSecret, {
        payment_method: { card },
      });
      if ((result as any).error) {
        setError(
          (result as any).error?.message || t("setup.cardConfirmError")
        );
        return;
      }
      const pmId = (result as any).setupIntent?.payment_method;
      if (!pmId) {
        setError(t("setup.cardInfoUnavailable"));
        return;
      }
      await PaymentApi.updatePaymentMethod(companyId, pmId);
      if (onSuccess) onSuccess();
      else onClose();
    } catch (e: any) {
      setError(e?.message || t("setup.networkError"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box display="flex" flexDirection="column" style={{ gap: 16 }}>
      <Typography variant="body2" color="textSecondary">
        {t("setup.instruction")}
      </Typography>
      <Box p={2} border={1} borderColor="divider" borderRadius={4}>
        <CardElement options={{ hidePostalCode: true }} />
      </Box>
      {error && <Alert severity="error">{error}</Alert>}
      <Box display="flex" justifyContent="flex-end" style={{ gap: 8 }}>
        <Button variant="outlined" disabled={loading} onClick={onClose}>
          {t("actions.cancel")}
        </Button>
        <Button
          variant="contained"
          color="primary"
          disabled={loading || !stripe}
          onClick={submit}
          startIcon={
            loading ? <CircularProgress size={16} color="inherit" /> : null
          }
        >
          {loading ? t("actions.registering") : t("actions.register")}
        </Button>
      </Box>
    </Box>
  );
};

export default Payment;
